import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { C, FONT, SectionHeader, formatDaySpan } from './adminShared';
import { useAuth } from '../../hooks/useAuth';
import {
  getBusinessCosts, createBusinessCost, updateBusinessCost, deactivateBusinessCost,
  getAdminUtmStats, getAdminRevenueSummary,
} from '../../utils/api';
import {
  PERIODS, FREQUENCY_LABELS, CURRENCIES, DEFAULT_RATES,
  costPerDayAud, money, periodDays, periodLabel,
} from './revenueShared';
import { CostRecalculateModal } from './CostRecalculateModal';
import { CostPlanner } from './CostPlanner';
import { DiscountWindowCard } from './DiscountWindowCard';

// ─── REVENUE & SPEND ─────────────────────────────────────────────────────────
// What goes out, what comes in, and what it looks like if the numbers change.
//
// ⚠ THE RULE THAT MATTERS MOST ON THIS SCREEN: actual and projected figures
// never share a card, a row, a total or a chart series. Real revenue is near
// zero and will stay low for a while; a projection sitting beside it looking
// similar would make the whole tab untrustworthy. Everything projected lives
// inside CostPlanner, behind its own border and its own "Projection" label.
//
// ⚠ EVERY LAYOUT HERE MUST WORK AT 375px WITHOUT SIDEWAYS SCROLL. The admin
// shell already scrolls sideways on a phone and that is a known annoyance; this
// tab must not add to it. That is why the cost rows are stacked cards rather
// than a table — six columns cannot fit, and a table would force the whole page
// wide.

// ── THE NEW-ROW HIGHLIGHT ────────────────────────────────────────────────────
// A CSS animation, not a transition. A transition needs two painted values in
// two separate frames to animate between, which means depending on animation
// frames firing — and they are throttled whenever the tab is not being drawn.
// A keyframe animation starts by itself, runs once, and cleans up after itself.
const FRESH_ROW_ANIM = 'ryFreshRow';
if (typeof document !== 'undefined' && !document.getElementById('ry-fresh-row-style')) {
  const tag = document.createElement('style');
  tag.id = 'ry-fresh-row-style';
  tag.textContent = `@keyframes ${FRESH_ROW_ANIM}{from{background:${C.amberBg}}to{background:#fff}}`;
  document.head.appendChild(tag);
}

const pill = (on) => ({
  padding: '6px 13px', borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: FONT,
  border: `1px solid ${on ? C.heading : C.border}`,
  background: on ? C.heading : C.card, color: on ? '#FFF8F7' : C.accent, cursor: 'pointer',
});
const input = {
  fontFamily: FONT, fontSize: 12, padding: '6px 8px',
  border: `1px solid ${C.border}`, borderRadius: 6, background: '#fff', color: C.text,
  width: '100%', boxSizing: 'border-box',
};
const panel = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 14px' };

/** A field Ben has edited by hand. It follows him, not the ledger, from now on. */
const YoursTag = () => (
  <span title="You changed this — a recalculation will not overwrite it"
    style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
      color: C.green, background: C.greenBg, border: '1px solid #BFE3D0',
      borderRadius: 4, padding: '1px 4px', marginLeft: 5, whiteSpace: 'nowrap' }}>yours</span>
);

function Tile({ label, value, sub, tone }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
      // 130 + 24px padding = 154, so two fit inside 343px of usable width on a
      // 375px phone. At 150 they wrapped to one per row and the headline took
      // five full screens to scroll past.
      padding: '12px 12px', flex: '1 1 130px', minWidth: 130 }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15, marginTop: 6,
        color: tone === 'out' ? C.red : tone === 'in' ? C.green : C.heading }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: C.accent, marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

/** One editable cost. Stacked so it fits a phone; every field saves on blur. */
function CostRow({ cost, groups, rates, view, days, canWrite, onSave, onRemove, onDragStart, dragging, isNew }) {
  const [draft, setDraft] = useState(cost);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState(null);
  const [picker, setPicker] = useState(false);
  // ── A CLICK MUST SHOW IT LANDED ───────────────────────────────────────────
  // Removal used to sit silent until the server replied, so Ben pressed again
  // thinking it had failed. `removing` both dims the row instantly and blocks a
  // second request for the same id.
  const [removing, setRemoving] = useState(false);
  // ⚠ THE LATCH IS A REF, NOT THE STATE. setRemoving() does not take effect until
  // React re-renders, so three clicks in one tick all read removing === false and
  // all three fired a request — measured, not theorised. A ref updates on the
  // spot, so the second click sees the latch the first one set.
  const removingRef = useRef(false);
  const nameRef = useRef(null);
  const rowRef  = useRef(null);
  // ── A NEW ROW ANNOUNCES ITSELF ────────────────────────────────────────────
  // It lands at the bottom of a long list, off-screen, so: scroll to it, put the
  // cursor in its name, and fade a tint OUT so it is obvious which one is new.
  //
  // The tint is painted imperatively rather than held in state. A state flag
  // never reached a committed render here — the row mounts with isNew ALREADY
  // true, and the flag was set and cleared without a tinted frame ever being
  // shown (measured: the inline background stayed white throughout). Setting the
  // colour, forcing a reflow, then transitioning to white owes nothing to
  // React's render timing, and it fades the right way round: bright, then away.
  useEffect(() => {
    if (!isNew) return;
    nameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const el = rowRef.current;
    if (el) el.style.animation = `${FRESH_ROW_ANIM} 1s ease-out 1`;
    const focusT = setTimeout(() => { nameRef.current?.focus(); nameRef.current?.select?.(); }, 250);
    return () => { clearTimeout(focusT); if (el) el.style.animation = ''; };
  }, [isNew]);
  // ── THE AMOUNT BOX SHOWS ROUNDED, EDITS EXACT ─────────────────────────────
  // amountText is null until Ben actually types. Null is the whole point: it is
  // how "he has not touched this" is distinguished from "he typed the same
  // number again", and only the first of those must stay out of the PATCH.
  const [amountText, setAmountText]   = useState(null);
  const [amountFocus, setAmountFocus] = useState(false);
  // `cost` is the value as loaded — the original every save is diffed against.
  useEffect(() => { setDraft(cost); setAmountText(null); }, [cost]);

  const over = new Set(cost.overridden_fields || []);
  const exactAmount = Number(cost.amount) || 0;

  // While he is typing, the derived lines follow what he has typed; otherwise
  // they follow the stored truth. Either way the arithmetic runs on the exact
  // number and only money() rounds, at the very end.
  const typed = amountText == null ? null : Number(amountText);
  const liveAmount = (amountFocus && typed != null && Number.isFinite(typed)) ? typed : exactAmount;
  const perDay = costPerDayAud(
    { amount: liveAmount, currency: draft.currency, frequency: draft.frequency }, rates,
  );

  /**
   * ⚠ SEND ONLY WHAT ACTUALLY CHANGED. The PATCH endpoint records a field as a
   * MANUAL OVERRIDE whenever the submitted value differs from the stored one,
   * and an overridden field is never refreshed from the ledger again — ever.
   *
   * So a form that posts the whole object is a trap here: the amount box
   * displays a rounded 6 for a stored 5.65, and editing only the name would
   * send amount: 6, the backend would see 6 !== 5.65, and it would silently
   * freeze a wrong figure permanently. Diffing against `cost` — the values as
   * loaded — is what stops that, and it is why the amount box never lets a
   * rounded number into its state in the first place.
   */
  const commit = async (patch) => {
    if (!canWrite) return;
    const changed = {};
    for (const [k, v] of Object.entries(patch)) {
      const before = k === 'amount' ? Number(cost[k]) : cost[k];
      if (v !== before) changed[k] = v;
    }
    if (Object.keys(changed).length === 0) return;   // nothing moved — send nothing
    setDraft(d => ({ ...d, ...changed }));
    setBusy(true); setErr(null);
    try { await onSave(cost.id, changed); }
    catch (e) { setErr(e.response?.data?.error || e.message); setDraft(cost); setAmountText(null); }
    finally { setBusy(false); }
  };

  return (
    <div
      ref={rowRef}
      draggable={canWrite}
      onDragStart={canWrite ? (e) => { e.dataTransfer.effectAllowed = 'move'; onDragStart(cost); } : undefined}
      style={{
        border: `1px solid ${C.border}`, borderRadius: 9, padding: '10px 10px', marginBottom: 8,
        background: dragging ? C.amberBg : '#fff',
        // 0.5 while removing: visibly on its way out. NOT a tick — a tick reads
        // as "saved and confirmed", which is the wrong meaning for a removal.
        opacity: removing ? 0.5 : busy ? 0.6 : 1,
        transition: 'background 900ms ease-out',
        cursor: canWrite ? 'grab' : 'default',
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <input ref={nameRef} value={draft.name} disabled={!canWrite}
              onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
              // e.target.value, NOT draft.name: React state is async, so a blur
              // in the same tick as the keystroke would read the previous name,
              // find nothing changed, and drop the edit without a word.
              onBlur={e => commit({ name: e.target.value })}
              style={{ ...input, fontWeight: 600, flex: '1 1 120px' }} />
            {over.has('name') && <YoursTag />}
          </div>
        </div>
        {canWrite && (
          <button
            disabled={removing}
            onClick={async () => {
              if (removingRef.current) return;   // second click does nothing
              removingRef.current = true;
              setRemoving(true); setErr(null);
              try {
                await onRemove(cost);            // row leaves the list on success
              } catch (e) {
                // A failed removal must not look like a successful one.
                removingRef.current = false;
                setRemoving(false);
                setErr(e.response?.data?.error || e.message || 'could not remove');
              }
            }}
            title={removing ? 'Removing…' : 'Remove — recoverable'}
            style={{ background: 'none', border: 'none',
              color: removing ? C.border : C.accent, fontSize: 16,
              cursor: removing ? 'default' : 'pointer', lineHeight: 1,
              padding: '4px 0', flexShrink: 0 }}>×</button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(86px, 1fr))', gap: 8, marginTop: 8 }}>
        <label style={{ fontSize: 9.5, color: C.accent }}>
          Amount {over.has('amount') && <YoursTag />}
          <input type="number" step="0.01" disabled={!canWrite}
            // Focused: the real stored figure, so he edits the truth rather than
            // a rounded approximation. Blurred and untouched: whole dollars.
            value={amountText != null ? amountText
                   : (amountFocus ? String(exactAmount) : String(Math.round(exactAmount)))}
            onFocus={() => setAmountFocus(true)}
            onChange={e => setAmountText(e.target.value)}
            onBlur={() => {
              setAmountFocus(false);
              if (amountText == null) return;              // never typed — send nothing
              const n = Number(amountText);
              // Typed the same number back, or typed nonsense: no PATCH, and the
              // box returns to the rounded display.
              if (!Number.isFinite(n) || n === exactAmount) { setAmountText(null); return; }
              commit({ amount: n });                       // stored exactly as typed
            }}
            style={{ ...input, marginTop: 3 }} />
        </label>
        <label style={{ fontSize: 9.5, color: C.accent }}>
          Currency {over.has('currency') && <YoursTag />}
          <select value={draft.currency} disabled={!canWrite}
            onChange={e => commit({ currency: e.target.value })} style={{ ...input, marginTop: 3 }}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 9.5, color: C.accent }}>
          Frequency {over.has('frequency') && <YoursTag />}
          <select value={draft.frequency} disabled={!canWrite}
            onChange={e => commit({ frequency: e.target.value })} style={{ ...input, marginTop: 3 }}>
            {Object.entries(FREQUENCY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 9,
        flexWrap: 'wrap', fontSize: 11, color: C.accent }}>
        <span>{money(perDay * 30.44, view, rates)}/mo</span>
        <strong style={{ color: C.red }}>{money(perDay * days, view, rates)} this {periodLabel()}</strong>
      </div>

      {/* THE NON-DRAG ALTERNATIVE. Drag-and-drop at 375px is unreliable, so the
          group name is always tappable and opens a plain picker. */}
      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {picker ? (
          <select autoFocus value={draft.group_label || 'Other'} disabled={!canWrite}
            onChange={e => { setPicker(false); commit({ group_label: e.target.value }); }}
            onBlur={() => setPicker(false)}
            style={{ ...input, width: 'auto', minWidth: 130 }}>
            {groups.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        ) : (
          <button onClick={() => canWrite && setPicker(true)} disabled={!canWrite}
            style={{ ...pill(false), padding: '3px 10px', fontSize: 10.5, cursor: canWrite ? 'pointer' : 'default' }}>
            {draft.group_label || 'Other'} {canWrite && '▾'}
          </button>
        )}
        {over.has('group_label') && <YoursTag />}
        {cost.source === 'ledger'
          ? <span style={{ fontSize: 9.5, color: C.accent }}>from your ledger</span>
          : <span style={{ fontSize: 9.5, color: C.accent }}>added by hand</span>}
      </div>

      {err && <div style={{ fontSize: 10.5, color: C.red, marginTop: 6 }}>{err}</div>}
    </div>
  );
}

export const AdminRevenue = () => {
  const { user } = useAuth();
  const canWrite = user?.role === 'owner';

  const [period, setPeriod] = useState('month');
  const [view, setView]     = useState('AUD');
  const [rates, setRates]   = useState(DEFAULT_RATES);

  const [costs, setCosts]       = useState(null);
  const [costsErr, setCostsErr] = useState(null);
  const [ads, setAds]           = useState(null);
  const [adsErr, setAdsErr]     = useState(null);
  const [rev, setRev]           = useState(null);
  const [revErr, setRevErr]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [recalc, setRecalc]     = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const [removed, setRemoved]   = useState([]);
  const [dragged, setDragged]   = useState(null);
  // `adding` blocks repeat presses — three fast clicks used to make three blank
  // rows. `newRowId` tells exactly one row to scroll itself into view and focus.
  const [adding, setAdding]     = useState(false);
  const addingRef               = useRef(false);   // synchronous latch — see CostRow
  const [newRowId, setNewRowId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const loadCosts = useCallback(async () => {
    try { const { data } = await getBusinessCosts(); setCosts(data); setCostsErr(null); }
    catch (e) { setCostsErr(e.response?.data?.error || e.message); }
  }, []);

  // ── allSettled, NOT all ────────────────────────────────────────────────────
  // AdminOverview.jsx:699 uses Promise.all and one revenue failure blanks that
  // whole tab. Here a dead panel shows its own error and the rest still works.
  useEffect(() => {
    let live = true;
    (async () => {
      const [c, a, r] = await Promise.allSettled([
        getBusinessCosts(), getAdminUtmStats(), getAdminRevenueSummary(),
      ]);
      if (!live) return;
      if (c.status === 'fulfilled') setCosts(c.value.data);
      else setCostsErr(c.reason?.response?.data?.error || c.reason?.message || 'unavailable');
      if (a.status === 'fulfilled') setAds(a.value.data?.metaAds || null);
      else setAdsErr(a.reason?.response?.data?.error || a.reason?.message || 'unavailable');
      if (r.status === 'fulfilled') setRev(r.value.data);
      else setRevErr(r.reason?.response?.data?.error || r.reason?.message || 'unavailable');
      setLoading(false);
    })();
    return () => { live = false; };
  }, []);

  const days = periodDays(period);
  // ── DATES, WHERE THERE ARE REAL ONES ──────────────────────────────────────
  // ⚠ THE PERIOD BUTTONS ARE NOT A DATE FILTER. They multiply a per-day rate up
  // to a period length, so "3 months" is a projected cost, not 1 Jul – 30 Sep.
  // Printing a date span under them would say the figures cover those dates,
  // and they do not — the two figures that DO have real dates carry them below.
  const todayISO = new Date().toISOString().slice(0, 10);
  const monthStartISO = `${todayISO.slice(0, 7)}-01`;
  const monthSpan = formatDaySpan(monthStartISO, todayISO);
  const plabel = PERIODS.find(p => p.id === period)?.label.toLowerCase() || 'month';

  const activeCosts = costs?.costs || [];
  const groupNames  = costs?.availableGroups || ['Software', 'Hosting', 'Staff', 'Marketing', 'Coaching', 'Other'];

  const fixedPerDay = useMemo(
    () => activeCosts.reduce((n, c) => n + costPerDayAud(c, rates), 0),
    [activeCosts, rates],
  );

  // Ad spend: the since-launch range is the clean window — spend and the funnel
  // cover the same period there. spendPerDay comes from the backend so the tab
  // and the Ad Metrics tab cannot quote different daily rates.
  const adRange = ads?.ranges?.find(r => r.key === 'since') || null;
  const adsPerDay = adRange?.totals?.spendPerDay || 0;
  // From the SAME object as the spend figures, so the label cannot drift.
  const adSpan = adRange ? formatDaySpan(adRange.since, adRange.until) : null;
  const outPerDay = fixedPerDay + adsPerDay;

  const byGroup = useMemo(() => {
    const m = new Map(groupNames.map(g => [g, []]));
    for (const c of activeCosts) {
      const g = c.group_label && m.has(c.group_label) ? c.group_label : 'Other';
      if (!m.has(g)) m.set(g, []);
      m.get(g).push(c);
    }
    return m;
  }, [activeCosts, groupNames]);

  const saveField = async (id, patch) => { await updateBusinessCost(id, patch); await loadCosts(); };

  // Throws on failure so the row can un-dim itself and show the error.
  const remove = async (cost) => { await deactivateBusinessCost(cost.id); await loadCosts(); };

  const addCost = async () => {
    if (addingRef.current) return;            // one press, one row
    addingRef.current = true;
    setAdding(true); setCostsErr(null);
    try {
      const { data } = await createBusinessCost({
        name: 'New cost', amount: 0, currency: 'AUD', frequency: 'monthly', group_label: 'Other',
      });
      await loadCosts();
      setNewRowId(data?.cost?.id || null);     // that row scrolls to itself and takes focus
    } catch (e) {
      setCostsErr(e.response?.data?.error || e.message);
    } finally {
      addingRef.current = false;
      setAdding(false);
    }
  };

  const loadRemoved = async () => {
    try {
      const { data } = await getBusinessCosts(true);
      setRemoved(data.costs.filter(c => !c.is_active));
      setShowRemoved(true);
    } catch (e) { setCostsErr(e.response?.data?.error || e.message); }
  };

  const dropInto = async (group) => {
    setDropTarget(null);
    const c = dragged; setDragged(null);
    if (!c || !canWrite || c.group_label === group) return;
    try { await saveField(c.id, { group_label: group }); }
    catch (e) { setCostsErr(e.response?.data?.error || e.message); }
  };

  const Err = ({ what, msg }) => (
    <div style={{ background: C.redBg, border: '1px solid #F0C4BC', borderRadius: 8,
      padding: '9px 11px', fontSize: 11.5, color: C.red, marginBottom: 10 }}>
      {what} unavailable — {msg}. The rest of this page still works.
    </div>
  );

  return (
    <div style={{ fontFamily: FONT, color: C.text }}>
      <SectionHeader title="Revenue & Spend" sub="What goes out, what comes in, and what it looks like if the numbers change." />

      {/* ── CONTROLS ───────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {PERIODS.map(p => (
          <button key={p.id} onClick={() => setPeriod(p.id)} style={pill(period === p.id)}>{p.label}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <span style={{ fontSize: 11, color: C.accent }}>Show me in</span>
        {['AUD', 'USD'].map(v => (
          <button key={v} onClick={() => setView(v)} style={{ ...pill(view === v), padding: '4px 12px', fontSize: 11 }}>{v}</button>
        ))}
        <span style={{ fontSize: 10, color: C.accent }}>display only — nothing stored changes</span>
      </div>

      {/* ── SECTION 1: HEADLINE ────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
        <Tile label={`Ads this ${plabel}`} tone="out"
          value={loading ? '…' : adsErr ? '—' : money(adsPerDay * days, view, rates)}
          sub={adsErr ? 'unavailable' : `${money(adsPerDay, view, rates)} a day · rate from ${adSpan || 'Meta'}`} />
        <Tile label={`Running costs this ${plabel}`} tone="out"
          value={loading ? '…' : costsErr ? '—' : money(fixedPerDay * days, view, rates)}
          sub={costsErr ? 'unavailable' : `${activeCosts.length} item${activeCosts.length === 1 ? '' : 's'}`} />
        <Tile label={`Everything out this ${plabel}`} tone="out"
          value={loading ? '…' : money(outPerDay * days, view, rates)}
          sub={`${money(outPerDay, view, rates)} a day, all in`} />
        <Tile label="Cost of a year" tone="out"
          value={loading ? '…' : money(outPerDay * 365, view, rates)} sub="if nothing changes" />
        {/* ACTUAL money. Deliberately NOT scaled by the period toggle — that
            toggle multiplies a daily rate, and multiplying real revenue by a
            period would be inventing data. */}
        <Tile label="Revenue this month" tone="in"
          value={loading ? '…' : revErr ? '—' : money(rev?.thisMonth ?? 0, view, rates)}
          sub={revErr ? 'unavailable' : `actual · RY funnel · ${monthSpan} · all time ${money(rev?.totalAllTime ?? 0, view, rates)}`} />
      </div>
      <div style={{ fontSize: 10, color: C.accent, marginBottom: 22, lineHeight: 1.55 }}>
        The period buttons scale a daily rate up to a period — they are not a date filter, so
        "3 months" is what three months would cost, not a window over past dates. Revenue is
        actual, for {monthSpan}.
      </div>

      {/* ── SECTION 2: MONEY GOING OUT ─────────────────────────────────────── */}
      <div style={{ fontFamily: "'Forum', serif", fontSize: '1.1rem', color: C.heading,
        letterSpacing: '0.03em', margin: '0 0 12px' }}>Money going out</div>

      {adsErr && <Err what="Ad spend" msg={adsErr} />}
      <div style={{ ...panel, marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: C.heading }}>Ads</div>
        <div style={{ fontSize: 10.5, color: C.accent, margin: '3px 0 12px' }}>
          Comes from Meta on its own, a few times a day. Read-only here.
          {adSpan && ` Daily rate averaged over ${adSpan}.`}
        </div>
        {!ads && !adsErr && <div style={{ fontSize: 11.5, color: C.accent }}>Loading…</div>}
        {adRange?.campaigns?.map((c, i) => (
          <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10,
            padding: '8px 0', borderTop: i ? `1px solid ${C.border}` : 'none', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: C.text }}>{c.label}</span>
            <span style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
              <span style={{ fontSize: 10.5, color: C.accent }}>
                {money(adRange.days ? c.spend / adRange.days : 0, view, rates)}/day
              </span>
              <strong style={{ fontSize: 12, color: C.red }}>
                {money((adRange.days ? c.spend / adRange.days : 0) * days, view, rates)}
              </strong>
            </span>
          </div>
        ))}
        {adRange && (
          <div style={{ borderTop: `2px solid ${C.heading}`, marginTop: 9, paddingTop: 9,
            display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: C.text }}>Ads, this {plabel}</span>
            <strong style={{ color: C.red }}>{money(adsPerDay * days, view, rates)}</strong>
          </div>
        )}
      </div>

      {costsErr && <Err what="Running costs" msg={costsErr} />}
      <div style={panel}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: C.heading }}>Running costs</div>
            <div style={{ fontSize: 10.5, color: C.accent, marginTop: 3 }}>
              Drag a cost to another group, or tap its group name to move it.
            </div>
          </div>
          {canWrite && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => setRecalc(true)} disabled={recalc}
                style={{ ...pill(true), opacity: recalc ? 0.6 : 1, cursor: recalc ? 'default' : 'pointer' }}>
                {recalc ? 'Reading your ledger…' : 'Recalculate from ledger'}
              </button>
              <button onClick={addCost} disabled={adding}
                style={{ ...pill(false), opacity: adding ? 0.6 : 1, cursor: adding ? 'default' : 'pointer' }}>
                {adding ? 'Adding…' : 'Add a cost'}
              </button>
            </div>
          )}
        </div>

        {!costs && !costsErr && <div style={{ fontSize: 11.5, color: C.accent, marginTop: 14 }}>Loading…</div>}

        {costs && activeCosts.length === 0 && (
          <div style={{ marginTop: 16, padding: '18px 14px', border: `1px dashed ${C.border}`,
            borderRadius: 10, textAlign: 'center', fontSize: 12, color: C.accent }}>
            No running costs yet. Press <strong style={{ color: C.heading }}>Recalculate from ledger</strong> to
            see what your ledger suggests, or add one by hand.
          </div>
        )}

        {costs && activeCosts.length > 0 && groupNames.map(g => {
          const rows = byGroup.get(g) || [];
          const sub = rows.reduce((n, c) => n + costPerDayAud(c, rates), 0);
          return (
            <div key={g}
              onDragOver={canWrite ? (e) => { e.preventDefault(); setDropTarget(g); } : undefined}
              onDragLeave={() => setDropTarget(t => (t === g ? null : t))}
              onDrop={canWrite ? (e) => { e.preventDefault(); dropInto(g); } : undefined}
              style={{
                marginTop: 16, padding: dropTarget === g ? '8px' : 0,
                border: dropTarget === g ? `2px dashed ${C.cta}` : '2px dashed transparent',
                borderRadius: 10, transition: 'border-color 0.12s',
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8,
                alignItems: 'baseline', marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: C.heading,
                  textTransform: 'uppercase', letterSpacing: '0.07em' }}>{g}</span>
                <span style={{ fontSize: 11, color: C.accent }}>
                  {rows.length === 0
                    ? 'empty'
                    : <>{rows.length} · <strong style={{ color: C.red }}>{money(sub * days, view, rates)}</strong> this {plabel}</>}
                </span>
              </div>
              {/* An empty group still shows — a section Ben made must not vanish
                  before he has dragged anything into it. */}
              {rows.length === 0 && (
                <div style={{ fontSize: 10.5, color: C.accent, border: `1px dashed ${C.border}`,
                  borderRadius: 8, padding: '10px 12px' }}>Nothing here yet — drop a cost in.</div>
              )}
              {rows.map(c => (
                <CostRow key={c.id} cost={c} groups={groupNames} rates={rates} view={view} days={days}
                  canWrite={canWrite} onSave={saveField} onRemove={remove}
                  onDragStart={setDragged} dragging={dragged?.id === c.id}
                  isNew={newRowId === c.id} />
              ))}
            </div>
          );
        })}

        {costs && activeCosts.length > 0 && (
          <div style={{ borderTop: `2px solid ${C.heading}`, marginTop: 14, paddingTop: 10,
            display: 'flex', justifyContent: 'space-between', fontSize: 13, flexWrap: 'wrap', gap: 8 }}>
            <span style={{ color: C.text }}>Running costs, this {plabel}</span>
            <strong style={{ color: C.red }}>{money(fixedPerDay * days, view, rates)}</strong>
          </div>
        )}

        {/* Removed rows are deactivated, never deleted — so they can be seen. */}
        <div style={{ marginTop: 14 }}>
          {!showRemoved
            ? <button onClick={loadRemoved} style={{ background: 'none', border: 'none', padding: 0,
                color: C.heading, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>
                Show removed costs
              </button>
            : (
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.heading }}>
                    Removed ({removed.length}) — kept, never deleted
                  </span>
                  <button onClick={() => setShowRemoved(false)} style={{ background: 'none', border: 'none',
                    color: C.accent, fontSize: 11, cursor: 'pointer', fontFamily: FONT, padding: 0 }}>hide</button>
                </div>
                {removed.map(c => (
                  <div key={c.id} style={{ fontSize: 11, color: C.accent, marginTop: 6, wordBreak: 'break-word' }}>
                    {c.name} — {money(c.monthlyEquivalent || 0, view, rates)}/mo · {c.group_label || 'Other'}
                  </div>
                ))}
                {removed.length === 0 && <div style={{ fontSize: 11, color: C.accent, marginTop: 6 }}>Nothing removed.</div>}
              </div>
            )}
        </div>
      </div>

      {/* ── SECTION 4: THE PLANNER (projected money, kept apart) ────────────── */}
      <CostPlanner outPerDayAud={outPerDay} view={view} rates={rates}
        onRatesChange={setRates} canWrite={canWrite} />

      {/* ── THE 48-HOUR DISCOUNT SWITCH ──────────────────────────────────────
          Below the planner, because it is an ACTION rather than a figure, and
          everything above it is something to read. Owner-only — the component
          renders nothing at all for a view-only admin, so the ownerMiddleware
          403 behind it can never surface as an error on screen. */}
      <DiscountWindowCard />

      {revErr && <div style={{ marginTop: 14 }}><Err what="Actual revenue" msg={revErr} /></div>}

      {recalc && (
        <CostRecalculateModal onClose={() => setRecalc(false)} onApplied={loadCosts} />
      )}
    </div>
  );
};
